import type { ILogbookRepository, PaginatedResponse } from '@yawi/domain';
import type {
  CheckpointQRResponse,
  CreateCheckpointDTO,
  CreateLogbookEntryDTO,
  LogbookEntry,
  LogbookParams,
  PatrolCheckpoint,
  PatrolRound,
  PatrolRoundParams,
  ScanCheckpointDTO,
  ScanCheckpointResponse,
  ShiftHandoverDTO,
  UpdateCheckpointDTO,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiLogbookRepository implements ILogbookRepository {
  private readonly basePath = 'v1/guard';

  // ========== LOGBOOK ENTRIES ==========

  async getEntries(params?: LogbookParams): Promise<PaginatedResponse<LogbookEntry>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.type) queryParams.set('type', params.type);
    if (params?.priority) queryParams.set('priority', params.priority);
    if (params?.fromDate) queryParams.set('dateFrom', params.fromDate);
    if (params?.toDate) queryParams.set('dateTo', params.toDate);
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/logbook/entries${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<LogbookEntry>>(url);
  }

  async getEntryById(id: string): Promise<LogbookEntry> {
    const client = getApiClient();
    return await client.get<LogbookEntry>(`${this.basePath}/logbook/entries/${id}`);
  }

  async getEntriesByShift(shiftId: string): Promise<LogbookEntry[]> {
    const client = getApiClient();
    const result = await client.get<PaginatedResponse<LogbookEntry>>(`${this.basePath}/logbook/shift/${shiftId}`);
    return result.data;
  }

  async createEntry(data: CreateLogbookEntryDTO): Promise<LogbookEntry> {
    const client = getApiClient();
    return await client.post<LogbookEntry>(`${this.basePath}/logbook/entries`, data);
  }

  // ========== SHIFT HANDOVER ==========

  async createShiftHandover(data: ShiftHandoverDTO): Promise<LogbookEntry> {
    const client = getApiClient();
    return await client.post<LogbookEntry>(`${this.basePath}/logbook/shift-handover`, data);
  }

  // ========== PATROL CHECKPOINTS ==========

  async getCheckpoints(activeOnly: boolean = true): Promise<PatrolCheckpoint[]> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();
    queryParams.set('activeOnly', String(activeOnly));

    return await client.get<PatrolCheckpoint[]>(
      `${this.basePath}/patrol/checkpoints?${queryParams.toString()}`
    );
  }

  async createCheckpoint(data: CreateCheckpointDTO): Promise<PatrolCheckpoint> {
    const client = getApiClient();
    return await client.post<PatrolCheckpoint>(`${this.basePath}/patrol/checkpoints`, data);
  }

  async updateCheckpoint(id: string, data: UpdateCheckpointDTO): Promise<PatrolCheckpoint> {
    const client = getApiClient();
    return await client.put<PatrolCheckpoint>(`${this.basePath}/patrol/checkpoints/${id}`, data);
  }

  async getCheckpointQR(id: string): Promise<CheckpointQRResponse> {
    const client = getApiClient();
    return await client.get<CheckpointQRResponse>(`${this.basePath}/patrol/checkpoints/${id}/qr`);
  }

  async deleteCheckpoint(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/patrol/checkpoints/${id}`);
  }

  // ========== PATROL ROUNDS ==========

  async getRounds(params?: PatrolRoundParams): Promise<PaginatedResponse<PatrolRound>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.status) queryParams.set('status', params.status);
    if (params?.guardId) queryParams.set('guardId', params.guardId);
    if (params?.shiftId) queryParams.set('shiftId', params.shiftId);
    if (params?.startDate) queryParams.set('dateFrom', params.startDate);
    if (params?.endDate) queryParams.set('dateTo', params.endDate);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/patrol/rounds${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<PatrolRound>>(url);
  }

  async getRoundById(id: string): Promise<PatrolRound> {
    const client = getApiClient();
    return await client.get<PatrolRound>(`${this.basePath}/patrol/rounds/${id}`);
  }

  async getActiveRound(): Promise<PatrolRound | null> {
    const client = getApiClient();
    return await client.get<PatrolRound | null>(`${this.basePath}/patrol/rounds/active`);
  }

  async startRound(notes?: string): Promise<PatrolRound> {
    const client = getApiClient();
    return await client.post<PatrolRound>(`${this.basePath}/patrol/rounds/start`, { notes });
  }

  async scanCheckpoint(roundId: string, data: ScanCheckpointDTO): Promise<ScanCheckpointResponse> {
    const client = getApiClient();
    return await client.post<ScanCheckpointResponse>(
      `${this.basePath}/patrol/rounds/${roundId}/scan`,
      data
    );
  }

  async completeRound(roundId: string, notes?: string): Promise<PatrolRound> {
    const client = getApiClient();
    return await client.post<PatrolRound>(
      `${this.basePath}/patrol/rounds/${roundId}/complete`,
      { notes }
    );
  }
}
